/**
 * Stash & retrieve public-interview branding (logo / company name) across pages.
 *
 * Used by the PUBLIC_INTERVIEW_AI flow:
 *   create-public.page → interview-room.page → result.page
 *
 * Why sessionStorage: each page currently re-fetches its own copy of `job_position`
 * via independent endpoints with slightly different shapes (some include
 * `logoImageUrl`, some only `company_logo`). Persisting the resolved logo from
 * the very first page keeps the visual identity consistent through the funnel
 * even when later API responses are missing that field, and survives full
 * page reloads within the tab.
 *
 * Scoped per (companyId, jobQuestionId) so multiple public links don't bleed
 * into each other.
 */

const STORAGE_PREFIX = "public_interview_branding";

export interface PublicInterviewBranding {
    logoUrl: string;
    companyName: string;
}

function buildKey(companyId: string | undefined, jobQuestionId: string | undefined): string | null {
    const c = String(companyId || "").trim();
    const j = String(jobQuestionId || "").trim();
    if (!c || !j) return null;
    return `${STORAGE_PREFIX}:${c}:${j}`;
}

export function setPublicInterviewBranding(
    companyId: string | undefined,
    jobQuestionId: string | undefined,
    branding: PublicInterviewBranding,
): void {
    const key = buildKey(companyId, jobQuestionId);
    if (!key) return;
    try {
        sessionStorage.setItem(key, JSON.stringify(branding));
    } catch {
        // sessionStorage unavailable (private mode / quota) — degrade silently;
        // downstream pages will just use their own API-derived value.
    }
}

export function getPublicInterviewBranding(
    companyId: string | undefined,
    jobQuestionId: string | undefined,
): PublicInterviewBranding | null {
    const key = buildKey(companyId, jobQuestionId);
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PublicInterviewBranding>;
        return {
            logoUrl: String(parsed?.logoUrl || ""),
            companyName: String(parsed?.companyName || ""),
        };
    } catch {
        return null;
    }
}
